import { useQuery } from '@tanstack/react-query';
import { readContract } from '@wagmi/core';

// eslint-disable-next-line deprecate/import
import { wagmiConfig } from '@shared/common/providers/LegacyWeb3Provider/wagmi';
import { DEFAULT_TARGET_CHAIN } from '@shared/constants/wallets';
import { convertTimestampSecondsToMs } from '@shared/helpers/date';

import type { AdjacentRound } from '../../model/AdjacentRound/adjacentRound';
import {
  getPredictionRoundTimeRemaining,
  PredictionRoundState,
} from '../../model/AdjacentRound/roundState';
import { PREDICTIONS_CONTRACT } from '../config/contract';
import { getQueryKey } from '../config/query';
import { dataMapper } from './dataMapper';

const fetchAdjacentRounds = async (): Promise<AdjacentRound[]> => {
  try {
    const rounds = await readContract(wagmiConfig, {
      ...PREDICTIONS_CONTRACT,
      chainId: DEFAULT_TARGET_CHAIN.id,
      functionName: 'getActiveLockedClosedRounds',
    });

    if (!Array.isArray(rounds)) {
      throw new Error('incorrect rounds');
    }

    return dataMapper(rounds);
  } catch (error) {
    console.error(error);
    return [];
  }
};

const DEFAULT_PREDICTION_ROUNDS_REFETCH_INTERVAL = convertTimestampSecondsToMs(10);

export const useAdjacentRounds = () => {
  return useQuery({
    queryKey: getQueryKey('adjacent-rounds'),
    queryFn: fetchAdjacentRounds,
    refetchInterval: ({ state: { data: rounds } }) => {
      const lockedRound = rounds?.find((round) => round.state === PredictionRoundState.LOCKED);

      if (!lockedRound) {
        return DEFAULT_PREDICTION_ROUNDS_REFETCH_INTERVAL;
      }

      return Math.min(
        getPredictionRoundTimeRemaining(lockedRound) || DEFAULT_PREDICTION_ROUNDS_REFETCH_INTERVAL,
        DEFAULT_PREDICTION_ROUNDS_REFETCH_INTERVAL,
      );
    },
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 0,
  });
};
